import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { Abilities, useAbility } from '@src/utils/useAbility'
import { makeStyles } from 'tss-react/mui'
import { Button, IconButton } from '@mui/material'
import { Add, Remove } from '@mui/icons-material'
import { useAddToCartWithGlobalSnackbar } from '@src/utils/useCart'
import { clamp } from 'lodash'
import { MaterialDetailFragment } from '@src/queries/MaterialDetailQuery.generated'
import { ProductDetailFragment } from '@src/fragments/ProductDetail.generated'
import { CartItemInput } from '@src/types/graphql-types'
import { CurrentStagedMaterial } from '@utils/analytics/types'
import { setAddToCartData } from '@utils/analytics/cartAndCheckoutEvents'
import { AddToCartPagesEnum } from '@utils/analytics/enums'
import { CartType } from '@src/routes'
import { SitePreference, useCurrentUser } from '@utils/useCurrentUser'

const QUANTITY_MIN = 1
const QUANTITY_MAX = 9999

const useStyles = makeStyles()((theme) => ({
  addToCartGroup: {
    display: 'flex',
    alignItems: 'center',
  },
  addToCartQuantity: {
    lineHeight: 1,
    fontSize: theme.typography.pxToRem(13),
    fontWeight: theme.typography.fontWeightMedium,
    textAlign: 'center',
    minWidth: 15,
  },
  addToCartQuantityButton: {
    '&:disabled': {
      opacity: 0.2,
      cursor: 'default',
      pointerEvents: 'auto',
    },
  },
  addToCartQuantityIcon: {
    fontSize: theme.typography.pxToRem(15),
  },
  addToCartButton: {
    minWidth: 0,
    fontSize: theme.typography.pxToRem(12),
    padding: '2px 10px',
    marginLeft: 4,
  },
}))

export interface HeaderSearchAddToCartProps {
  material: undefined | null | MaterialDetailFragment
  quantity: number
  product: undefined | null | ProductDetailFragment
  setQuantity: (quantity: number) => void
  onAddToCartClick?: () => void
  onSuccess?: (cartType: CartType | null | undefined) => void
}

export const HeaderSearchAddToCart: React.FC<HeaderSearchAddToCartProps> = ({
  material,
  quantity,
  setQuantity,
  onAddToCartClick,
  onSuccess,
}) => {
  const { classes } = useStyles()
  const { formatMessage } = useIntl()
  const { getSitePreference } = useCurrentUser()
  const [isAddingToCart, setIsAddingToCart] = useState(false)
  const addToCart = useAddToCartWithGlobalSnackbar()
  const increment = (howMany: number) => {
    setQuantity(clamp(quantity + howMany, QUANTITY_MIN, QUANTITY_MAX))
  }
  const { authorized } = useAbility(Abilities.canAddToCart)
  const hideAddToCartForPrepackItems = getSitePreference(
    SitePreference.HideAddToCartForPrepackItems
  )

  if (!material) {
    return null
  }

  const isMarketplace = !!material.isMarketplace

  return (
    <>
      {!hideAddToCartForPrepackItems ? (
        <div className={classes.addToCartGroup}>
          <IconButton
            size="small"
            className={classes.addToCartQuantityButton}
            disabled={isAddingToCart || quantity <= QUANTITY_MIN}
            onClick={() => increment(-1)}
          >
            <Remove className={classes.addToCartQuantityIcon} />
          </IconButton>
          <span className={classes.addToCartQuantity}>{quantity}</span>
          <IconButton
            size="small"
            className={classes.addToCartQuantityButton}
            disabled={isAddingToCart || quantity >= QUANTITY_MAX}
            onClick={() => increment(1)}
          >
            <Add className={classes.addToCartQuantityIcon} />
          </IconButton>
          {authorized && (
            <Button
              color="secondary"
              variant="contained"
              size="small"
              disabled={isAddingToCart}
              className={classes.addToCartButton}
              onClick={async () => {
                if (onAddToCartClick) onAddToCartClick()
                setIsAddingToCart(true)
                const cartItems: CartItemInput[] = [
                  {
                    materialNumber: material.number,
                    quantity,
                    marketplaceOfferId: material.marketplaceOfferId,
                  },
                ]
                const materialPricing: CurrentStagedMaterial[] = [
                  {
                    material,
                    quantity,
                  },
                ]
                setAddToCartData(
                  cartItems,
                  materialPricing,
                  AddToCartPagesEnum.HeaderSearch
                )
                const cartType = await addToCart(
                  cartItems,
                  undefined,
                  undefined,
                  isMarketplace
                )
                setIsAddingToCart(false)
                setQuantity(1)
                if (onSuccess) onSuccess(cartType)
              }}
            >
              {formatMessage({
                id: 'ADD_TO_CART',
                defaultMessage: 'Add to Cart',
              })}
            </Button>
          )}
        </div>
      ) : null}
    </>
  )
}
